import React, { useEffect, useRef } from "react";
import * as d3 from "d3";

const AdaptiveGauge = ({
  size = "small", // "small" or "large"
  value = 58,
  min = 0,
  max = 100,
  label = "Campaign Magnet Score",
  backgroundColor = "#FFFDF2",
  // New Configurable Props
  needleLengthScale = 0.7, 
  dotRadius = 5,
}) => {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!svgRef.current) return;

    d3.select(svgRef.current).selectAll("*").remove();

    const isSmall = size === "small";
    const width = isSmall ? 78 : 280;
    const height = isSmall ? 68 : 170;
    const centerX = width / 2;
    const centerY = isSmall ? 44 : 130;

    const startAngle = -Math.PI * 0.55;
    const endAngle = Math.PI * 0.55;
    const totalSweep = endAngle - startAngle;

    const scale = d3.scaleLinear()
      .domain([min, max])
      .range([startAngle, endAngle]);

    const clampedValue = Math.max(min, Math.min(max, value));
    const targetAngle = scale(clampedValue);

    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height);

    const g = svg.append("g")
      .attr("transform", `translate(${centerX}, ${centerY})`);

    const colorShades = ["#FCD12A", "#FFA726", "#D97D34", "#A75D24"];

    let dynamicFillColor = "#D97D34";
    if (clampedValue <= 25) dynamicFillColor = "#FCD12A";
    else if (clampedValue <= 50) dynamicFillColor = "#FFA726";
    else if (clampedValue <= 75) dynamicFillColor = "#D97D34";
    else dynamicFillColor = "#A75D24";

    if (isSmall) {
      const rMiddleOuter = 34;
      const rMiddleInner = 29.5;
      const rProgressOuter = 27.5;
      const rProgressInner = 18.5;

      const smallSegments = Array.from({ length: 4 }).map((_, i) => ({
        startAngle: startAngle + (totalSweep / 4) * i,
        endAngle: startAngle + (totalSweep / 4) * (i + 1),
        color: colorShades[i]
      }));
      
      const outerArcGenerator = d3.arc().innerRadius(rMiddleInner).outerRadius(rMiddleOuter);
      g.selectAll(".outer-shade-seg").data(smallSegments).enter().append("path")
        .attr("d", d => outerArcGenerator(d)).attr("fill", d => d.color);

      const innerArcGenerator = d3.arc().innerRadius(rProgressInner).outerRadius(rProgressOuter);
      g.append("path")
        .datum({ startAngle: startAngle, endAngle: targetAngle })
        .attr("d", innerArcGenerator).attr("fill", dynamicFillColor);

      g.append("text").attr("text-anchor", "middle").attr("y", 5)
        .style("font-family", "sans-serif").style("font-weight", "bold")
        .style("font-size", "14px").text(clampedValue);
    } else {
      const outerRadius = 130;
      const largeSegments = Array.from({ length: 4 }).map((_, i) => ({
        startAngle: startAngle + (totalSweep / 4) * i,
        endAngle: startAngle + (totalSweep / 4) * (i + 1),
        color: colorShades[i]
      }));

      const thinOuterArc = d3.arc().innerRadius(outerRadius - 9).outerRadius(outerRadius - 2);
      g.selectAll(".thin-seg").data(largeSegments).enter().append("path")
        .attr("d", d => thinOuterArc(d)).attr("fill", d => d.color);

      const mainTrackOuter = outerRadius - 16;
      const mainTrackInner = outerRadius - 48;
      const thickArc = d3.arc().innerRadius(mainTrackInner).outerRadius(mainTrackOuter);

      g.append("path").datum({ startAngle: startAngle, endAngle: endAngle }).attr("d", thickArc).attr("fill", "#F4F4F2");
      g.append("path").datum({ startAngle: startAngle, endAngle: targetAngle }).attr("d", thickArc).attr("fill", dynamicFillColor);

      // --- Updated Needle Setup ---
      const needleLength = (mainTrackInner + 6) * needleLengthScale;
      const geoAngle = targetAngle - Math.PI / 2;

      const tipX = needleLength * Math.cos(geoAngle);
      const tipY = needleLength * Math.sin(geoAngle);
      const baseLeftX = dotRadius * Math.cos(geoAngle - Math.PI / 2);
      const baseLeftY = dotRadius * Math.sin(geoAngle - Math.PI / 2);
      const baseRightX = dotRadius * Math.cos(geoAngle + Math.PI / 2);
      const baseRightY = dotRadius * Math.sin(geoAngle + Math.PI / 2);

      g.append("path").attr("d", `M ${baseLeftX} ${baseLeftY} L ${tipX} ${tipY} L ${baseRightX} ${baseRightY} Z`).attr("fill", "#000000");
      g.append("circle").attr("r", dotRadius).attr("fill", "#000000");
      g.append("text").attr("text-anchor", "middle").attr("y", 38).style("font-family", "sans-serif").style("font-size", "28px").text(clampedValue);
    }
  }, [size, value, min, max, label, backgroundColor, needleLengthScale, dotRadius]);

  return React.createElement("svg", { ref: svgRef });
};

export default AdaptiveGauge;